<template>
  <main v-if="entry" class="article-page section-shell">
    <div class="site-container">
      <nav class="article-breadcrumb">
        <router-link to="/insights">Insights</router-link>
        <span>/</span>
        <span>{{ entry.title }}</span>
      </nav>

      <header class="article-header">
        <div>
          <p class="eyebrow">{{ entry.category }}</p>
          <h1>{{ entry.title }}</h1>
          <p class="article-header__meta">{{ publishedLabel }} - {{ entry.readingTime }} min de lectura</p>
          <p class="article-header__summary">{{ entry.summary }}</p>
        </div>
        <OptimizedImage class="article-hero-image" :src="entry.image" :alt="entry.title" />
      </header>

      <section class="article-layout">
        <article class="article-body">
          <template v-for="(block, index) in blocks">
            <h2 v-if="block.type === 'heading' && block.level === 2" :key="`h2-${index}`" v-html="block.html" />
            <h3 v-else-if="block.type === 'heading' && block.level === 3" :key="`h3-${index}`" v-html="block.html" />
            <p v-else-if="block.type === 'paragraph'" :key="`p-${index}`" v-html="block.html" />
            <ul v-else-if="block.type === 'list'" :key="`ul-${index}`">
              <li v-for="(item, itemIndex) in block.items" :key="`item-${index}-${itemIndex}`" v-html="item" />
            </ul>
            <hr v-else-if="block.type === 'divider'" :key="`hr-${index}`" />
          </template>
        </article>

        <aside class="article-sidebar">
          <div class="sidebar-card">
            <p class="eyebrow">Consulta técnica</p>
            <h2>Si este artículo coincide con un problema real en planta, podemos revisarlo juntos.</h2>
            <a href="https://calendly.com/germanlamela98/30min" target="_blank" rel="noopener" class="btn-primary">
              Agendar reunión
            </a>
          </div>
          <div class="sidebar-card">
            <p class="eyebrow">Relacionados</p>
            <router-link
              v-for="related in relatedEntries"
              :key="related.slug"
              :to="`/insights/${related.slug}`"
              class="related-link"
            >
              <strong>{{ related.title }}</strong>
              <span>{{ related.category }}</span>
            </router-link>
          </div>
        </aside>
      </section>
    </div>
  </main>

  <main v-else class="article-page section-shell">
    <div class="site-container article-empty">
      <p class="eyebrow">Artículo no encontrado</p>
      <h1>No encontramos esa publicación.</h1>
      <router-link to="/insights" class="btn-primary">Volver a insights</router-link>
    </div>
  </main>
</template>

<script>
import OptimizedImage from '@/pages/components/OptimizedImage.vue';
import { blogEntries } from '@/generated/blogEntries';
import { renderMarkdown } from '@/utils/blogMarkdown';
import { setSeoMeta } from '@/utils/seo';
import { normalizeEntry } from '@/utils/text';

export default {
  name: 'InsightArticlePage',
  components: {
    OptimizedImage
  },
  computed: {
    entry() {
      const foundEntry = blogEntries.find(item => item.slug === this.$route.params.slug);
      return foundEntry ? normalizeEntry(foundEntry) : null;
    },
    blocks() {
      return this.entry ? renderMarkdown(this.entry.body) : [];
    },
    relatedEntries() {
      if (!this.entry) {
        return [];
      }

      return blogEntries
        .filter(item => item.slug !== this.entry.slug)
        .map(normalizeEntry)
        .slice(0, 3);
    },
    publishedLabel() {
      if (!this.entry) {
        return '';
      }

      return new Intl.DateTimeFormat('es-UY', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      }).format(new Date(this.entry.publishedAt));
    }
  },
  watch: {
    entry: {
      immediate: true,
      handler() {
        this.applySeo();
      }
    }
  },
  methods: {
    applySeo() {
      if (!this.entry) {
        setSeoMeta({
          title: 'Artículo no encontrado | JL Consultorías',
          description: 'La publicación solicitada no está disponible.',
          url: window.location.href
        });
        return;
      }

      setSeoMeta({
        title: `${this.entry.title} | JL Consultorías`,
        description: this.entry.summary,
        url: `${window.location.origin}/insights/${this.entry.slug}`,
        image: `${window.location.origin}${this.entry.image}`,
        type: 'article',
        structuredData: {
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: this.entry.title,
          description: this.entry.summary,
          image: [`${window.location.origin}${this.entry.image}`],
          datePublished: this.entry.publishedAt,
          author: {
            '@type': 'Person',
            name: 'Julio César Lamela'
          },
          publisher: {
            '@type': 'Organization',
            name: 'JL Consultorías',
            logo: {
              '@type': 'ImageObject',
              url: `${window.location.origin}/img/logo-jl.png`
            }
          },
          mainEntityOfPage: `${window.location.origin}/insights/${this.entry.slug}`
        }
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.article-breadcrumb {
  display: flex;
  gap: 0.4rem;
  padding-top: 4rem;
  color: var(--muted-color);
  font-family: var(--font-mono);
  font-size: 0.78rem;
  text-transform: uppercase;
}

.article-header,
.article-layout {
  display: grid;
  gap: 2rem;
}

.article-header {
  margin: 2rem 0 3rem;
}

.article-header__meta {
  color: var(--muted-color);
  font-family: var(--font-mono);
  font-size: 0.8rem;
  text-transform: uppercase;
}

.article-header__summary {
  color: var(--text-color);
}

.article-hero-image :deep(img) {
  filter: brightness(0.58);
}

.article-body,
.sidebar-card {
  background: var(--surface-color);
  border: 1px solid rgba(59, 73, 76, 0.7);
}

.article-body {
  padding: 2rem;
}

.article-body h2,
.article-body h3 {
  margin-bottom: 0.8rem;
}

.article-body ul {
  padding-left: 1.25rem;
}

.article-body hr {
  margin: 2rem 0;
  border: 0;
  border-top: 1px solid rgba(59, 73, 76, 0.7);
}

.article-sidebar {
  display: grid;
  gap: 1rem;
  align-content: start;
}

.sidebar-card {
  display: grid;
  gap: 1rem;
  padding: 1.5rem;
}

.related-link {
  display: grid;
  gap: 0.25rem;
  padding-top: 0.85rem;
  border-top: 1px solid rgba(59, 73, 76, 0.7);
}

.related-link span {
  color: var(--muted-color);
  font-family: var(--font-mono);
  font-size: 0.76rem;
  text-transform: uppercase;
}

.article-empty {
  display: grid;
  place-items: center;
  gap: 1rem;
  min-height: 65vh;
  text-align: center;
}

@media (min-width: 992px) {
  .article-header {
    grid-template-columns: minmax(0, 1fr) minmax(22rem, 0.8fr);
    align-items: center;
  }

  .article-layout {
    grid-template-columns: minmax(0, 1fr) 20rem;
    align-items: start;
  }

  .article-sidebar {
    position: sticky;
    top: 7rem;
  }
}
</style>
